/**
 * Dynamic Ink Segmenter (Scale-Invariant Universal DLBS)
 *
 * Provides universal, device- and handwriting-independent mathematical step segmentation:
 *  1. Dynamically estimates student's handwriting scale (median character height H_scale).
 *  2. Identifies structural landmarks: parallel equals signs (=), fraction bars, and denominators.
 *  3. Clusters strokes into mathematical step lines by baseline alignment and local spatial connection.
 *  4. Preserves line breaks: tall brackets, fraction expressions, and subscripts NEVER swallow adjacent step lines.
 *  5. Orders steps strictly top-to-bottom: Step 1, Step 2, Step 3...
 */

export const DEFAULT_SEGMENTER_CONFIG = {
  // Baseline matching multiplier (relative to handwriting scale H_scale)
  baselineToleranceRatio: 1.0,

  // Fraction parameters
  fractionBarMinAspectRatio: 2.2,
  fractionBarMaxSlope: 0.30,
  fractionBarMaxHeightRatio: 0.70,
  fractionBarMinWidthRatio: 1.10,
  fractionBarMaxWidthRatio: 7.0,
  fractionReachRatio: 1.8,

  // Subscript parameter (under large operators like \lim)
  subscriptMaxGapRatio: 1.6,

  // Merging threshold for lines
  maxLineMergeRatio: 0.85,

  // Crop rendering options (generous padding ensures exponents/subscripts on edges are never clipped)
  cropPadding: 28,
  minCropDimension: 48
};

/**
 * Calculates bounding box and geometric properties for an ink stroke.
 */
export function getStrokeGeometry(stroke) {
  const points = Array.isArray(stroke?.points) ? stroke.points : [];
  if (points.length < 2) return null;

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let pathLength = 0;

  for (let i = 0; i < points.length; i += 2) {
    const x = Number(points[i]);
    const y = Number(points[i + 1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;

    if (i >= 2) {
      const prevX = Number(points[i - 2]);
      const prevY = Number(points[i - 1]);
      pathLength += Math.hypot(x - prevX, y - prevY);
    }
  }

  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;

  const strokeWidth = Number(stroke?.width) || 2.5;
  const pad = strokeWidth / 2;
  const bMinX = minX - pad;
  const bMinY = minY - pad;
  const bMaxX = maxX + pad;
  const bMaxY = maxY + pad;
  const width = Math.max(1, bMaxX - bMinX);
  const height = Math.max(1, bMaxY - bMinY);
  const startX = Number(points[0]);
  const startY = Number(points[1]);
  const endX = Number(points[points.length - 2]);
  const endY = Number(points[points.length - 1]);
  const deltaX = Math.abs(endX - startX);
  const deltaY = Math.abs(endY - startY);

  return {
    id: stroke?.id || `s_${Math.random().toString(36).slice(2, 8)}`,
    stroke,
    points,
    minX: bMinX,
    minY: bMinY,
    maxX: bMaxX,
    maxY: bMaxY,
    width,
    height,
    aspectRatio: width / height,
    pathLength,
    startX,
    startY,
    endX,
    endY,
    deltaX,
    deltaY,
    centerX: (bMinX + bMaxX) / 2,
    centerY: (bMinY + bMaxY) / 2,
    isFractionBar: false,
    isEqualsBar: false,
    tool: stroke?.tool || 'pen'
  };
}

/**
 * Combines multiple bounding boxes into an encompassing bounding box.
 */
export function unionBounds(boundsList) {
  const valid = boundsList.filter(Boolean);
  if (!valid.length) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0, centerX: 0, centerY: 0 };
  }

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const b of valid) {
    if (b.minX < minX) minX = b.minX;
    if (b.minY < minY) minY = b.minY;
    if (b.maxX > maxX) maxX = b.maxX;
    if (b.maxY > maxY) maxY = b.maxY;
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2
  };
}

/**
 * Estimates the student's natural handwriting scale (median character height).
 */
export function estimateHandwritingScale(geoms) {
  const validHeights = geoms
    .filter((s) => s.height >= 10 && s.height <= 120 && s.aspectRatio < 3.0)
    .map((s) => s.height)
    .sort((a, b) => a - b);

  if (!validHeights.length) return 32;
  return Math.max(18, Math.min(75, validHeights[Math.floor(validHeights.length / 2)]));
}

/**
 * Checks if a pen stroke has been substantially covered by subsequent eraser strokes.
 */
export function isStrokeErased(penStroke, allStrokes, threshold = 0.60) {
  if (!penStroke?.points || penStroke.points.length < 2) return true;
  const rawList = Array.isArray(allStrokes) ? allStrokes : [];
  const penRaw = penStroke.stroke || penStroke;
  const penIdx = rawList.indexOf(penRaw);
  if (penIdx < 0) return false;

  const laterErasers = rawList.slice(penIdx + 1).filter(
    (s) => s?.tool === "eraser" && Array.isArray(s?.points) && s.points.length >= 2
  );
  if (!laterErasers.length) return false;

  const pts = penStroke.points;
  let hitCount = 0;
  const totalPts = Math.floor(pts.length / 2);
  if (totalPts === 0) return true;

  for (let i = 0; i < pts.length; i += 2) {
    const px = pts[i];
    const py = pts[i + 1];
    let pointErased = false;
    for (const er of laterErasers) {
      const eWidth = Number(er.width) || 28;
      const radius = eWidth / 2 + 2;
      const ePts = er.points;
      for (let j = 0; j < ePts.length; j += 2) {
        if (Math.hypot(px - ePts[j], py - ePts[j + 1]) <= radius) {
          pointErased = true;
          break;
        }
      }
      if (pointErased) break;
    }
    if (pointErased) hitCount++;
  }

  return hitCount / totalPts >= threshold;
}

/**
 * Detects equals sign bars (=) and fraction bars.
 */
export function detectStructuralStrokes(geoms, hScale, config = DEFAULT_SEGMENTER_CONFIG) {
  const maxEqualsSlope = 0.38;
  const maxEqualsHeight = Math.max(14, 0.80 * hScale);
  const minEqualsAspect = 1.4;

  // Pair parallel horizontal strokes into equals signs (=)
  for (let i = 0; i < geoms.length; i++) {
    const s1 = geoms[i];
    const s1Slope = s1.deltaY / Math.max(1, s1.deltaX);
    if (s1.aspectRatio < minEqualsAspect || s1.width > 3.5 * hScale || s1.height > maxEqualsHeight || s1Slope > maxEqualsSlope) continue;
    for (let j = i + 1; j < geoms.length; j++) {
      const s2 = geoms[j];
      const s2Slope = s2.deltaY / Math.max(1, s2.deltaX);
      if (s2.aspectRatio < minEqualsAspect || s2.width > 3.5 * hScale || s2.height > maxEqualsHeight || s2Slope > maxEqualsSlope) continue;
      const vDist = Math.abs(s1.centerY - s2.centerY);
      const hDist = Math.abs(s1.centerX - s2.centerX);
      const hOverlap = Math.min(s1.maxX, s2.maxX) - Math.max(s1.minX, s2.minX);
      const widthRatio = Math.min(s1.width, s2.width) / Math.max(s1.width, s2.width);

      // Slopes should be reasonably parallel
      if (Math.abs(s1Slope - s2Slope) > 0.30) continue;

      // Check: does any other stroke lie vertically sandwiched directly between s1 and s2?
      const minYPair = Math.min(s1.centerY, s2.centerY);
      const maxYPair = Math.max(s1.centerY, s2.centerY);
      const minXPair = Math.min(s1.minX, s2.minX);
      const maxXPair = Math.max(s1.maxX, s2.maxX);
      const hasSandwichedStroke = geoms.some(
        (other) =>
          other !== s1 &&
          other !== s2 &&
          (other.width >= 0.6 * hScale || other.height >= 0.6 * hScale) &&
          other.minY > minYPair - 2 &&
          other.maxY < maxYPair + 2 &&
          Math.min(other.maxX, maxXPair) - Math.max(other.minX, minXPair) >= 0.3 * other.width
      );
      if (hasSandwichedStroke) continue;

      if (
        vDist <= 1.35 * hScale &&
        vDist >= 3 &&
        hDist <= 0.9 * hScale &&
        hOverlap >= 0.30 * Math.min(s1.width, s2.width) &&
        widthRatio >= 0.35
      ) {
        s1.isEqualsBar = true;
        s2.isEqualsBar = true;
      }
    }
  }

  // Identify fraction bars: wide, flat horizontal strokes (NOT equals, NOT minus signs, NOT tall characters, NOT huge polygon lines)
  const maxBarHeight = Math.max(14, (config.fractionBarMaxHeightRatio || 0.70) * hScale);
  const minBarWidth = Math.max(25, (config.fractionBarMinWidthRatio || 1.10) * hScale);
  const maxBarWidth = (config.fractionBarMaxWidthRatio || 7.0) * hScale;

  for (const s of geoms) {
    if (s.isEqualsBar) {
      s.isFractionBar = false;
      continue;
    }
    const slope = s.deltaY / Math.max(1, s.deltaX);
    s.isFractionBar =
      s.height <= maxBarHeight &&
      s.width >= minBarWidth &&
      s.width <= maxBarWidth &&
      s.aspectRatio >= (config.fractionBarMinAspectRatio || 2.2) &&
      slope <= (config.fractionBarMaxSlope || 0.30);
  }
}

/**
 * Checks if two strokes physically touch or come within a spatial radius of each other.
 */
export function strokesTouch(s1, s2, threshold = 28) {
  const p1 = s1?.points || s1?.stroke?.points;
  const p2 = s2?.points || s2?.stroke?.points;
  if (!p1 || !p2) return false;
  for (let i = 0; i < p1.length; i += 2) {
    const x1 = p1[i], y1 = p1[i + 1];
    for (let j = 0; j < p2.length; j += 2) {
      const x2 = p2[j], y2 = p2[j + 1];
      if (Math.hypot(x1 - x2, y1 - y2) <= threshold) return true;
    }
  }
  return false;
}

/**
 * Helper to check whether stroke belongs to an existing step line.
 */
export function strokeBelongsToLine(geom, line, config = DEFAULT_SEGMENTER_CONFIG) {
  if (!geom || !line) return false;
  const hScale = line.hScale || 32;

  // Fraction attachment (numerator or denominator)
  if (line.fractionBars && line.fractionBars.length) {
    for (const bar of line.fractionBars) {
      const withinBarX = geom.centerX >= bar.minX - 0.5 * hScale && geom.centerX <= bar.maxX + 0.5 * hScale;
      const isDenom = geom.minY >= bar.minY - 4 && geom.minY <= bar.maxY + config.fractionReachRatio * hScale;
      const isNum = geom.maxY <= bar.maxY + 4 && geom.maxY >= bar.minY - config.fractionReachRatio * hScale;
      if (withinBarX && (isDenom || isNum)) {
        return true;
      }
    }
  }

  // Subscript directly under a symbol (e.g. x -> 1 under lim)
  if (Array.isArray(line.strokes)) {
    for (const lst of line.strokes) {
      const hOverlap = Math.min(geom.maxX, lst.maxX) - Math.max(geom.minX, lst.minX);
      const vGapUnder = geom.minY - lst.maxY;
      if (hOverlap >= 0.25 * Math.min(geom.width, lst.width) && vGapUnder >= -4 && vGapUnder <= config.subscriptMaxGapRatio * hScale) {
        return true;
      }
    }
  }

  // Left-margin new step check:
  const isLeftEdge = geom.minX <= line.bounds.minX + 1.5 * hScale;
  const diff = Math.abs(geom.centerY - (line.baselineY || (line.bounds.minY + line.bounds.maxY) / 2));
  if (isLeftEdge && geom.isEqualsBar && diff >= 0.7 * hScale) {
    return false;
  }

  // Main baseline proximity
  return diff <= config.baselineToleranceRatio * hScale;
}

/**
 * Evaluates whether two adjacent clusters should be merged because they represent
 * an erroneously split single math step (e.g. subscript under \lim, denominator in nested fraction,
 * tall bracket, exponent bar, or horizontal continuation).
 */
export function shouldMergeClusters(a, b, hScale) {
  if (!a || !b || !a.bounds || !b.bounds) return false;
  // If one is a diagram and one is math:
  if ((a.contentKind === "diagram" || b.contentKind === "diagram") && a.contentKind !== b.contentKind) {
    const mathClust = a.contentKind === "math" ? a : b;
    const diagClust = a.contentKind === "diagram" ? a : b;

    const diagHOverlap = Math.min(mathClust.bounds.maxX, diagClust.bounds.maxX) - Math.max(mathClust.bounds.minX, diagClust.bounds.minX);
    const diagVOverlap = Math.min(mathClust.bounds.maxY, diagClust.bounds.maxY) - Math.max(mathClust.bounds.minY, diagClust.bounds.minY);
    const bLineDiff = Math.abs((mathClust.baselineY || 0) - (diagClust.baselineY || 0));

    // A false diagram:
    // 1) The math cluster horizontally encloses or significantly overlaps the diagram cluster on the same row, OR
    // 2) The diagram cluster is immediately contiguous horizontally with the math cluster's equals sign on the same row, OR
    // 3) The diagram cluster touches math operators/fraction bars
    const isHorizontallySandwiched =
      mathClust.bounds.minX <= diagClust.bounds.minX + 2.0 * hScale &&
      mathClust.bounds.maxX >= diagClust.bounds.maxX - 2.0 * hScale;

    const isHorizontallyContiguous =
      diagHOverlap >= 25 ||
      isHorizontallySandwiched ||
      (mathClust.hasEquals && Math.abs(diagClust.bounds.minX - mathClust.bounds.maxX) <= 2.5 * hScale);

    const isSameRow = bLineDiff <= 3.2 * hScale || diagVOverlap > 0;

    const touchesMathElements =
      mathClust.hasEquals ||
      mathClust.hasFractionBar ||
      diagClust.hasFractionBar ||
      (Array.isArray(diagClust.strokes) && diagClust.strokes.some((s) => s.isFractionBar || s.isEqualsBar));

    if (isHorizontallyContiguous && isSameRow && touchesMathElements) {
      // False diagram! It belongs to the math expression line.
      return true;
    }

    return false;
  }

  const ba = a.bounds;
  const bb = b.bounds;

  const aBaseline = Number.isFinite(a.baselineY) ? a.baselineY : (ba.minY + ba.maxY) / 2;
  const bBaseline = Number.isFinite(b.baselineY) ? b.baselineY : (bb.minY + bb.maxY) / 2;
  const baselineDiff = Math.abs(bBaseline - aBaseline);

  // Horizontal overlap
  const hOverlap = Math.min(ba.maxX, bb.maxX) - Math.max(ba.minX, bb.minX);
  const minWidth = Math.min(ba.width, bb.width);
  const hOverlapRatio = minWidth > 0 ? hOverlap / minWidth : 0;

  // Vertical overlap
  const vOverlap = Math.min(ba.maxY, bb.maxY) - Math.max(ba.minY, bb.minY);
  const minHeight = Math.min(ba.height, bb.height);
  const vOverlapRatio = minHeight > 0 ? vOverlap / minHeight : 0;

  // Vertical gap (positive means separated, negative means overlapping)
  const vGap = bb.minY - ba.maxY;

  // If both clusters have equals signs:
  // Each equals sign starts a new step, unless they are on the exact same horizontal baseline
  if (a.hasEquals && b.hasEquals) {
    if (baselineDiff <= 0.8 * hScale && (vOverlap > 0 || vGap <= 0.3 * hScale)) {
      return true;
    }
    return false;
  }

  // If two clusters have baselines separated by >= 2.0 * hScale, and BOTH have their own distinct
  // left-margin roots (e.g. both start near the left margin):
  // They are distinct consecutive steps, NOT a split line!
  const bothHaveLeftRoots =
    Math.abs(ba.minX - bb.minX) <= 1.5 * hScale && Math.min(ba.minX, bb.minX) <= 320;
  if (baselineDiff >= 2.0 * hScale && bothHaveLeftRoots) {
    // Only merge if one is almost completely vertically inside the other
    if (minHeight <= 0 || vOverlap / minHeight < 0.65) {
      return false;
    }
  }

  // Condition 1: Small baseline difference (< 1.5 * hScale)
  // Two consecutive mathematical steps have baselines separated by >= 1.5 * hScale
  if (baselineDiff < 1.5 * hScale && (hOverlapRatio >= 0.15 || hOverlap >= 25)) {
    return true;
  }

  // Condition 2: Deep vertical overlap/nesting
  if (vOverlapRatio >= 0.30 && (hOverlapRatio >= 0.20 || hOverlap >= 30)) {
    // Unless baselines are far apart and both start at left margin
    if (baselineDiff < 2.5 * hScale || !bothHaveLeftRoots) {
      return true;
    }
  }

  // Condition 3: Proximity continuation where the lower cluster has no equals sign
  if (!b.hasEquals) {
    // If lower cluster is an indented fragment (subscript, denominator, bracket bottom)
    if (vGap <= 1.2 * hScale && (hOverlapRatio >= 0.25 || hOverlap >= 30)) {
      if (baselineDiff <= 2.2 * hScale || bb.minX > ba.minX + 1.2 * hScale) {
        return true;
      }
    }
    // Baseline difference within single multi-line expression
    if (baselineDiff <= 1.8 * hScale && (hOverlapRatio >= 0.30 || hOverlap >= 35)) {
      return true;
    }
  }

  // Condition 4: Top has equals, bottom has no equals, indented continuation
  if (a.hasEquals && !b.hasEquals) {
    if (vGap <= 1.4 * hScale && (hOverlapRatio >= 0.25 || hOverlap >= 30)) {
      if (baselineDiff <= 2.2 * hScale || bb.minX > ba.minX + 1.5 * hScale) {
        return true;
      }
    }
  }

  // Condition 5: Both have equals, but baseline difference is too small to be separate lines
  if (a.hasEquals && b.hasEquals) {
    if (baselineDiff <= 0.8 * hScale && (vOverlap > 0 || vGap <= 0.3 * hScale)) {
      return true;
    }
  }

  return false;
}

/**
 * Fuses two erroneously split clusters into one unified step cluster.
 */
export function fuseTwoClusters(a, b, hScale) {
  const allStrokes = [...a.strokes];
  const strokeIds = new Set(a.strokes.map((s) => s.id || s.stroke?.id || s));
  for (const s of b.strokes) {
    const id = s.id || s.stroke?.id || s;
    if (!strokeIds.has(id)) {
      strokeIds.add(id);
      allStrokes.push(s);
    }
  }

  const minX = Math.min(a.bounds.minX, b.bounds.minX);
  const minY = Math.min(a.bounds.minY, b.bounds.minY);
  const maxX = Math.max(a.bounds.maxX, b.bounds.maxX);
  const maxY = Math.max(a.bounds.maxY, b.bounds.maxY);

  const baselineY = a.hasEquals
    ? a.baselineY
    : b.hasEquals
    ? b.baselineY
    : (minY + maxY) / 2;

  return {
    id: a.id,
    stepNo: a.stepNo,
    strokes: allStrokes,
    bounds: {
      minX,
      minY,
      maxX,
      maxY,
      width: Math.max(1, maxX - minX),
      height: Math.max(1, maxY - minY)
    },
    baselineY,
    fractionBars: [...(a.fractionBars || []), ...(b.fractionBars || [])],
    hasFractionBar: Boolean(a.hasFractionBar || b.hasFractionBar),
    hasEquals: Boolean(a.hasEquals || b.hasEquals),
    contentKind:
      a.hasEquals || b.hasEquals || a.hasFractionBar || b.hasFractionBar
        ? "math"
        : a.contentKind === "diagram" && b.contentKind === "diagram"
        ? "diagram"
        : "math",
    revision: Math.max(a.revision || 1, b.revision || 1) + 1
  };
}

/**
 * Recursively checks and merges erroneously split bounding boxes until convergence.
 */
export function mergeErroneouslySplitClusters(clusters, hScale) {
  if (!Array.isArray(clusters) || clusters.length <= 1) {
    return Array.isArray(clusters) ? clusters : [];
  }

  let merged = [...clusters];
  let changed = true;
  let passes = 0;
  const MAX_PASSES = 12;

  while (changed && passes < MAX_PASSES) {
    changed = false;
    passes++;
    merged.sort((a, b) => a.bounds.minY - b.bounds.minY);

    for (let i = 0; i < merged.length - 1; i++) {
      const a = merged[i];
      const b = merged[i + 1];

      if (shouldMergeClusters(a, b, hScale)) {
        const fused = fuseTwoClusters(a, b, hScale);
        merged.splice(i, 2, fused);
        changed = true;
        break; // restart pass after merge
      }
    }
  }

  return merged.map((c, idx) => ({
    ...c,
    id: `step_${idx + 1}`,
    stepNo: idx + 1
  }));
}

/**
 * Segment all strokes into ordered, horizontal mathematical Step Lines

 * using Universal Anchor-Bounded Step Partitioning (ABSP).
 * Guaranteed:
 *  - Scale-invariant and device-independent (adapts to student's writing size).
 *  - 2D mathematical structures (exponents, fractions, tall brackets) remain unified in their equation step.
 *  - Distinct equations on separate baselines are cleanly partitioned.
 *  - Output steps are ordered strictly 1, 2, 3... from top to bottom.
 */
export function segmentStrokes(rawStrokes, options = {}) {
  const config = { ...DEFAULT_SEGMENTER_CONFIG, ...(options.config || {}) };
  const allRaw = Array.isArray(rawStrokes) ? rawStrokes : [];

  // Filter active pen strokes that have not been erased
  const activeRawStrokes = allRaw.filter((s) => {
    if (s?.tool === "eraser") return false;
    if (!Array.isArray(s?.points) || s.points.length < 4) return false;
    return !isStrokeErased(s, allRaw);
  });

  const strokes = activeRawStrokes.map(getStrokeGeometry).filter(Boolean);
  if (!strokes.length) return [];

  const hScale = estimateHandwritingScale(strokes);
  detectStructuralStrokes(strokes, hScale, config);

  // 1. Group parallel equals bars into Equals Landmarks for math equations FIRST
  const equalsLandmarks = [];
  const processedEquals = new Set();
  strokes.filter((s) => s.isEqualsBar).forEach((s1) => {
    if (processedEquals.has(s1.id)) return;
    const pair = [s1];
    processedEquals.add(s1.id);
    strokes.filter((s) => s.isEqualsBar && !processedEquals.has(s.id)).forEach((s2) => {
      if (
        Math.abs(s1.centerY - s2.centerY) <= 1.25 * hScale &&
        Math.abs(s1.centerX - s2.centerX) <= 0.9 * hScale
      ) {
        pair.push(s2);
        processedEquals.add(s2.id);
      }
    });
    equalsLandmarks.push({
      isEquals: true,
      strokes: pair,
      minX: Math.min(...pair.map((s) => s.minX)),
      maxX: Math.max(...pair.map((s) => s.maxX)),
      minY: Math.min(...pair.map((s) => s.minY)),
      maxY: Math.max(...pair.map((s) => s.maxY)),
      centerX: pair.reduce((sum, s) => sum + s.centerX, 0) / pair.length,
      centerY: pair.reduce((sum, s) => sum + s.centerY, 0) / pair.length
    });
  });

  // 2. Detect 2D Geometric Diagram Clusters (if any):
  const isOnMathEquationLine = (s) =>
    s.isEqualsBar ||
    s.isFractionBar ||
    equalsLandmarks.some((eq) => Math.abs(eq.centerY - s.centerY) <= 1.4 * hScale);

  // Geometric figure strokes are long line segments (sides of polygons, arcs)
  const isGeometricStroke = (s) =>
    !isOnMathEquationLine(s) &&
    (s.pathLength >= 3.5 * hScale || Math.max(s.width, s.height) >= 3.5 * hScale);

  const longStrokes = strokes.filter(isGeometricStroke);
  const diagramClusters = [];
  const visited = new Set();
  const touchThreshold = Math.max(30, 1.4 * hScale);

  for (const seed of longStrokes) {
    if (visited.has(seed.id)) continue;

    const comp = [seed];
    visited.add(seed.id);
    const queue = [seed];

    while (queue.length > 0) {
      const curr = queue.shift();
      for (const other of strokes) {
        if (visited.has(other.id) || isOnMathEquationLine(other)) continue;
        if (strokesTouch(curr, other, touchThreshold)) {
          visited.add(other.id);
          comp.push(other);
          queue.push(other);
        }
      }
    }

    const cMinY = Math.min(...comp.map((st) => st.minY));
    const cMaxY = Math.max(...comp.map((st) => st.maxY));
    const cMinX = Math.min(...comp.map((st) => st.minX));
    const cMaxX = Math.max(...comp.map((st) => st.maxX));
    const cH = cMaxY - cMinY;
    const cW = cMaxX - cMinX;

    const longCount = comp.filter(isGeometricStroke).length;
    if (
      cH >= 3.0 * hScale &&
      cW >= 2.5 * hScale &&
      longCount >= 2 &&
      cW / Math.max(1, cH) >= 0.25 &&
      cH / Math.max(1, cW) >= 0.25
    ) {
      // Must not contain equals or fraction bars
      if (comp.some((s) => s.isFractionBar || s.isEqualsBar)) continue;

      // Absorb small labels inside or immediately touching the geometry
      for (const s of strokes) {
        if (visited.has(s.id) || isOnMathEquationLine(s)) continue;
        if (
          s.centerX >= cMinX - 1.0 * hScale &&
          s.centerX <= cMaxX + 1.0 * hScale &&
          s.centerY >= cMinY - 0.5 * hScale &&
          s.centerY <= cMaxY + 0.5 * hScale
        ) {
          visited.add(s.id);
          comp.push(s);
        }
      }

      diagramClusters.push({
        isDiagram: true,
        strokes: comp,
        minX: cMinX,
        maxX: cMaxX,
        minY: cMinY,
        maxY: cMaxY,
        centerY: (cMinY + cMaxY) / 2
      });
    }
  }

  const diagramStrokeIds = new Set(diagramClusters.flatMap((d) => d.strokes.map((st) => st.id)));
  const mathStrokes = strokes.filter((s) => !diagramStrokeIds.has(s.id));

  // 3. Deduplicate equals landmarks on the exact same line (vDist <= 0.8 * hScale)
  const mathAnchors = [];
  equalsLandmarks.sort((a, b) => a.centerY - b.centerY);
  for (const eq of equalsLandmarks) {
    if (diagramStrokeIds.has(eq.strokes[0]?.id)) continue;
    const existing = mathAnchors.find((ma) => Math.abs(ma.centerY - eq.centerY) <= 0.8 * hScale);
    if (existing) {
      existing.strokes.push(...eq.strokes);
      existing.minY = Math.min(existing.minY, eq.minY);
      existing.maxY = Math.max(existing.maxY, eq.maxY);
      existing.minX = Math.min(existing.minX, eq.minX);
      existing.maxX = Math.max(existing.maxX, eq.maxX);
      existing.centerY = (existing.minY + existing.maxY) / 2;
    } else {
      mathAnchors.push({ ...eq });
    }
  }

  // 4. Candidate line roots at the left margin for math lines WITHOUT '='
  if (mathStrokes.length) {
    const mathMinX = Math.min(...mathStrokes.map((s) => s.minX));
    const leftMarginZone = mathMinX + 2.5 * hScale;

    mathStrokes.forEach((s) => {
      if (s.isEqualsBar) return;
      if (s.minX > leftMarginZone) return;

      const belongsToExisting = mathAnchors.some(
        (a) =>
          Math.abs(s.centerY - a.centerY) <= 1.6 * hScale ||
          (s.minX >= a.minX - 0.5 * hScale && s.minY >= a.minY - 0.5 * hScale && s.minY <= a.maxY + 2.2 * hScale)
      );
      if (belongsToExisting) return;

      // Subscript / denominator check
      const hasParentAbove = mathStrokes.some(
        (other) =>
          other !== s &&
          Math.min(s.maxX, other.maxX) - Math.max(s.minX, other.minX) >= 0.25 * Math.min(s.width, other.width) &&
          s.minY >= other.minY &&
          s.minY - other.maxY <= 1.8 * hScale
      );
      if (hasParentAbove) return;

      const isUnderFraction = mathStrokes.some(
        (bar) =>
          bar.isFractionBar &&
          bar !== s &&
          s.minY >= bar.minY - 4 &&
          s.minY <= bar.maxY + 2.5 * hScale &&
          Math.min(s.maxX, bar.maxX) - Math.max(s.minX, bar.minX) >= 0.25 * Math.min(s.width, bar.width)
      );
      if (isUnderFraction) return;

      if (s.isFractionBar) {
        const hasNumeratorAbove = mathStrokes.some(
          (other) =>
            other !== s &&
            other.maxY <= s.maxY + 4 &&
            other.maxY >= s.minY - 2.0 * hScale &&
            Math.min(s.maxX, other.maxX) - Math.max(s.minX, other.minX) >= 0.25 * Math.min(s.width, other.width)
        );
        if (hasNumeratorAbove) return;
      }

      mathAnchors.push({
        isEquals: false,
        strokes: [s],
        minX: s.minX,
        maxX: s.maxX,
        minY: s.minY,
        maxY: s.maxY,
        centerX: s.centerX,
        centerY: s.centerY
      });
    });
  }

  // Combine diagrams and math line anchors
  const allAnchors = [...diagramClusters, ...mathAnchors];

  // Fallback if no anchors found at all
  if (!allAnchors.length) {
    const sortedByY = [...strokes].sort((a, b) => a.centerY - b.centerY);
    let currentGroup = [sortedByY[0]];
    for (let i = 1; i < sortedByY.length; i++) {
      const prev = currentGroup[currentGroup.length - 1];
      const curr = sortedByY[i];
      if (curr.minY - prev.maxY >= 1.5 * hScale || Math.abs(curr.centerY - prev.centerY) >= 2.5 * hScale) {
        allAnchors.push({
          strokes: currentGroup,
          minY: Math.min(...currentGroup.map((st) => st.minY)),
          maxY: Math.max(...currentGroup.map((st) => st.maxY)),
          centerY: currentGroup.reduce((sum, st) => sum + st.centerY, 0) / currentGroup.length
        });
        currentGroup = [curr];
      } else {
        currentGroup.push(curr);
      }
    }
    if (currentGroup.length) {
      allAnchors.push({
        strokes: currentGroup,
        minY: Math.min(...currentGroup.map((st) => st.minY)),
        maxY: Math.max(...currentGroup.map((st) => st.maxY)),
        centerY: currentGroup.reduce((sum, st) => sum + st.centerY, 0) / currentGroup.length
      });
    }
  }

  allAnchors.sort((a, b) => a.centerY - b.centerY);

  // Consolidate anchors: distinct math lines are separated by >= 2.4 * hScale.
  // Anchors closer than 2.4 * hScale or with overlapping Y extents belong to the same logical math expression.
  const consolidatedAnchors = [];
  for (const anchor of allAnchors) {
    const prev = consolidatedAnchors[consolidatedAnchors.length - 1];
    if (
      prev &&
      !prev.isDiagram &&
      !anchor.isDiagram &&
      Math.abs(anchor.centerY - prev.centerY) < 1.4 * hScale
    ) {
      prev.strokes.push(...(anchor.strokes || []));
      prev.minY = Math.min(prev.minY, anchor.minY);
      prev.maxY = Math.max(prev.maxY, anchor.maxY);
      prev.minX = Math.min(prev.minX, anchor.minX);
      prev.maxX = Math.max(prev.maxX, anchor.maxX);
      prev.centerY = (prev.minY + prev.maxY) / 2;
      prev.isEquals = prev.isEquals || anchor.isEquals;
    } else {
      consolidatedAnchors.push(anchor);
    }
  }

  const activeAnchors = consolidatedAnchors.length > 0 ? consolidatedAnchors : allAnchors;

  // Partition boundaries: Compute Y split planes between adjacent anchors
  const splitYs = [];
  for (let i = 0; i < activeAnchors.length - 1; i++) {
    const upper = activeAnchors[i];
    const lower = activeAnchors[i + 1];
    const boundary =
      upper.maxY && lower.minY && upper.maxY < lower.minY
        ? (upper.maxY + lower.minY) / 2
        : (upper.centerY + lower.centerY) / 2;
    splitYs.push(boundary);
  }

  // Assign strokes to step lines
  const stepBuckets = Array.from({ length: Math.max(1, activeAnchors.length) }, () => []);

  for (const s of strokes) {
    // If stroke falls into a diagram's 2D envelope, assign to diagram
    const diagIdx = activeAnchors.findIndex(
      (la) =>
        la.isDiagram &&
        s.centerY >= la.minY - 0.5 * hScale &&
        s.centerY <= la.maxY + 0.5 * hScale &&
        s.minX >= la.minX - 1.5 * hScale &&
        s.maxX <= la.maxX + 1.5 * hScale
    );
    if (diagIdx >= 0) {
      stepBuckets[diagIdx].push(s);
      continue;
    }

    let assignedIdx = 0;
    if (splitYs.length > 0) {
      for (let i = 0; i < splitYs.length; i++) {
        if (s.centerY > splitYs[i]) {
          assignedIdx = i + 1;
        }
      }
    }
    stepBuckets[assignedIdx].push(s);
  }

  // Collect eraser strokes to include in visual crops
  const rawIndexMap = new Map();
  allRaw.forEach((s, idx) => rawIndexMap.set(s, idx));
  const eraserStrokes = allRaw.filter((s) => s?.tool === "eraser" && Array.isArray(s?.points) && s.points.length >= 4);

  // Produce ordered step clusters
  const clusters = [];
  for (let i = 0; i < stepBuckets.length; i++) {
    const bucketStrokes = stepBuckets[i];
    if (!bucketStrokes.length) continue;

    const bMinX = Math.min(...bucketStrokes.map((s) => s.minX));
    const bMinY = Math.min(...bucketStrokes.map((s) => s.minY));
    const bMaxX = Math.max(...bucketStrokes.map((s) => s.maxX));
    const bMaxY = Math.max(...bucketStrokes.map((s) => s.maxY));

    // Find any eraser strokes that intersect or fall within this step's envelope
    const relevantErasers = eraserStrokes.filter((er) => {
      const eGeom = getStrokeGeometry(er);
      if (!eGeom) return false;
      return (
        eGeom.maxX >= bMinX - 20 &&
        eGeom.minX <= bMaxX + 20 &&
        eGeom.maxY >= bMinY - 20 &&
        eGeom.minY <= bMaxY + 20
      );
    });

    // Chronologically order active pen strokes and eraser strokes for visual accuracy
    const allClusterStrokes = [...bucketStrokes, ...relevantErasers.map(getStrokeGeometry).filter(Boolean)].sort(
      (a, b) => {
        const rawA = a.stroke || a;
        const rawB = b.stroke || b;
        return (rawIndexMap.get(rawA) ?? 0) - (rawIndexMap.get(rawB) ?? 0);
      }
    );

    const anchor = activeAnchors[i];
    const bBaselineY = anchor ? anchor.centerY : (bMinY + bMaxY) / 2;
    const isDiagram = Boolean(anchor?.isDiagram);

    clusters.push({
      id: `step_${clusters.length + 1}`,
      stepNo: clusters.length + 1,
      strokes: allClusterStrokes,
      bounds: {
        minX: bMinX,
        minY: bMinY,
        maxX: bMaxX,
        maxY: bMaxY,
        width: Math.max(1, bMaxX - bMinX),
        height: Math.max(1, bMaxY - bMinY)
      },
      baselineY: bBaselineY,
      fractionBars: bucketStrokes.filter((s) => s.isFractionBar),
      hasFractionBar: bucketStrokes.some((s) => s.isFractionBar),
      hasEquals: bucketStrokes.some((s) => s.isEqualsBar),
      hScale,
      contentKind: isDiagram ? "diagram" : "math",
      revision: allClusterStrokes.length
    });
  }

  return mergeErroneouslySplitClusters(clusters, hScale);
}

/**
 * Crops and renders an individual step cluster's strokes to a tight PNG data URL.
 * Renders pen strokes and eraser strokes in exact chronological order with proper composite modes.
 */
export function renderClusterImageData(cluster, options = {}) {
  if (!cluster || !Array.isArray(cluster.strokes) || !cluster.strokes.length) {
    return { dataUrl: "", bounds: null, clearLine: true, width: 0, height: 0 };
  }

  const pad = Number.isFinite(options.padding) ? options.padding : DEFAULT_SEGMENTER_CONFIG.cropPadding;
  const b = cluster.bounds;
  const minX = Math.max(0, Math.floor(b.minX - pad));
  const minY = Math.max(0, Math.floor(b.minY - pad));
  const maxX = Math.ceil(b.maxX + pad);
  const maxY = Math.ceil(b.maxY + pad);

  const width = Math.max(DEFAULT_SEGMENTER_CONFIG.minCropDimension, maxX - minX);
  const height = Math.max(DEFAULT_SEGMENTER_CONFIG.minCropDimension, maxY - minY);

  const canvas = options.canvas || (typeof document !== "undefined" ? document.createElement("canvas") : null);
  if (!canvas) {
    return { dataUrl: "", bounds: { minX, minY, maxX, maxY }, clearLine: false, width, height };
  }

  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return { dataUrl: "", bounds: { minX, minY, maxX, maxY }, clearLine: false, width, height };
  }

  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (const geom of cluster.strokes) {
    const rawStroke = geom.stroke || geom;
    const points = geom.points || rawStroke?.points || [];
    if (points.length < 4) continue;

    const isEraser = rawStroke?.tool === "eraser";
    ctx.globalCompositeOperation = isEraser ? "destination-out" : "source-over";
    ctx.strokeStyle = isEraser ? "rgba(0,0,0,1)" : "#000000";
    ctx.lineWidth = Number(rawStroke?.width) || (isEraser ? 28 : 2.5);

    ctx.beginPath();
    ctx.moveTo(points[0] - minX, points[1] - minY);
    for (let i = 2; i < points.length; i += 2) {
      ctx.lineTo(points[i] - minX, points[i + 1] - minY);
    }
    ctx.stroke();
  }

  return {
    dataUrl: canvas.toDataURL("image/png"),
    bounds: { minX, minY, maxX, maxY, width, height },
    clearLine: false,
    width,
    height
  };
}

/**
 * Temporal Step Box Manager (Incremental State-Preserving Step Continuity)
 *
 * Observes ink events across confirmed step boxes:
 *  - Rule A (In-Place Edit / Erasure / Addition): If strokes fall inside an existing step box's
 *    vertical territory, that box absorbs the strokes and recomputes bounds. No new step is created.
 *  - Rule B (Step Extension): Horizontally continuing a step line expands that step box.
 *  - Rule C (New Step Introduction): A new step is introduced ONLY when strokes are drawn clearly
 *    below the bottommost confirmed step box (or starts near the left margin / equals sign).
 *  - Rule D (Erasure Pruning & Destination-Out): Empty boxes (all strokes erased) are cleanly removed,
 *    and intersecting eraser strokes are attached in chronological order for canvas rendering.
 *
 * @param {Array} prevClusters Previous confirmed step clusters
 * @param {Array} allRawStrokes Full stroke history on the canvas
 * @param {Object} options Segmenter configuration options
 * @returns {Array} Updated step clusters with continuity preserved
 */
export function updateStepClustersIncremental(prevClusters = [], allRawStrokes = [], options = {}) {
  const freshClusters = segmentStrokes(allRawStrokes, options);
  if (!freshClusters.length) return [];

  const prevList = Array.isArray(prevClusters) ? prevClusters : [];
  const usedPrevIds = new Set();

  return freshClusters.map((fc, idx) => {
    const matchedPrev = prevList.find((pc) => {
      if (!pc || !pc.bounds || usedPrevIds.has(pc.id)) return false;
      const vOverlap = Math.min(pc.bounds.maxY, fc.bounds.maxY) - Math.max(pc.bounds.minY, fc.bounds.minY);
      const minH = Math.min(pc.bounds.height, fc.bounds.height);
      const isOverlap = minH > 0 && vOverlap / minH >= 0.35;
      const isNearCenter = Math.abs(pc.baselineY - fc.baselineY) <= 1.8 * fc.hScale;
      return isOverlap || isNearCenter;
    });

    if (matchedPrev) {
      usedPrevIds.add(matchedPrev.id);
    }

    const stepId = matchedPrev?.id || `step_${idx + 1}`;
    return {
      ...fc,
      id: stepId,
      stepNo: idx + 1,
      revision: (matchedPrev?.revision || 0) + (fc.strokes.length !== (matchedPrev?.strokes?.length || 0) ? 1 : 0)
    };
  });
}


